import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { Box, FormLabel, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { calculateDistanceBetweenCoordinates } from '@/domain/position-geocode/utils/calculate-distance/calculate-distance';
import { editTourTravel } from '@/domain/project-tour-ontour/actions/edit-tour-travel.action';
import { TourTravel } from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import {
  selectProjectTourOnTourEvents,
  selectProjectTourOnTourPositionHistoryListOutcoming,
  selectProjectTourOnTourPositionsHistoryIncoming as selectProjectTourOnTourPositionHistoryListIncoming,
} from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSPositionGeocodeField } from '@/ui/components/input/FSPositionGeocodeField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { EventPositionField } from './EventPositionField';
import { PositionFillFromHistoryButton } from './PositionFillFromHistoryButton';

type FormInputs = {
  name: string;
  fromPosition: PositionGeocode | undefined;
  toPosition: PositionGeocode | undefined;
  travelDistance: number;
};

// ── Main component ────────────────────────────────────────────────────────────

export function TourTravelDataForm({
  tourTravel,
  onTotalDistanceChange,
  onDelete,
}: {
  tourTravel: TourTravel;
  onTotalDistanceChange: () => void;
  onDelete?: () => void;
}) {
  const hasData = !!(
    tourTravel.name ||
    tourTravel.fromPosition ||
    tourTravel.toPosition
  );
  const [isEditing, setIsEditing] = useState(!hasData);

  if (isEditing) {
    return (
      <TourTravelEditForm
        tourTravel={tourTravel}
        onSaved={() => {
          onTotalDistanceChange();
          setIsEditing(false);
        }}
      />
    );
  }

  return (
    <TourTravelReadView
      tourTravel={tourTravel}
      onEdit={() => setIsEditing(true)}
      onDelete={onDelete}
    />
  );
}

// ── Read-only view ────────────────────────────────────────────────────────────

function TourTravelReadView({
  tourTravel,
  onEdit,
  onDelete,
}: {
  tourTravel: TourTravel;
  onEdit: () => void;
  onDelete?: () => void;
}) {
  const tourEvents = useAppSelector(selectProjectTourOnTourEvents);
  const fromEvent = tourEvents.find((e) => e.id === tourTravel.fromEventId);
  const toEvent = tourEvents.find((e) => e.id === tourTravel.toEventId);

  const fromLabel = getFromLabel(tourTravel, fromEvent);
  const toLabel = getToLabel(tourTravel, toEvent);
  const routeLabel = [fromLabel, toLabel].filter(Boolean).join(' → ');

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        border: `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
        borderRadius: '8px',
        p: 1.5,
        minWidth: '52rem',
      }}
    >
      <Stack sx={{ flex: 1, gap: 0.25 }}>
        {tourTravel.name && (
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {tourTravel.name}
          </Typography>
        )}
        {routeLabel && (
          <Typography sx={{ fontSize: '0.875rem' }}>{routeLabel}</Typography>
        )}
        {tourTravel.travelDistance > 0 && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {tourTravel.travelDistance} km
          </Typography>
        )}
      </Stack>
      <IconButton size="small" onClick={onEdit}>
        <EditOutlined sx={{ fontSize: 16 }} />
      </IconButton>
      {onDelete && (
        <IconButton size="small" onClick={onDelete}>
          <DeleteOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Stack>
  );
}

// ── Edit form ─────────────────────────────────────────────────────────────────

function TourTravelEditForm({
  tourTravel,
  onSaved,
}: {
  tourTravel: TourTravel;
  onSaved: () => void;
}) {
  const dispatch = useAppDispatch();
  const tourEvents = useAppSelector(selectProjectTourOnTourEvents);
  const positionHistoryListIncoming = useAppSelector(
    selectProjectTourOnTourPositionHistoryListIncoming,
  );
  const positionHistoryListOutcoming = useAppSelector(
    selectProjectTourOnTourPositionHistoryListOutcoming,
  );

  const fromEvent = tourEvents.find((e) => e.id === tourTravel.fromEventId);
  const toEvent = tourEvents.find((e) => e.id === tourTravel.toEventId);

  const [isDistanceComputing, setIsDistanceComputing] = useState(false);

  const { handleSubmit, register, control, watch, setValue, formState } =
    useForm<FormInputs>({
      defaultValues: {
        name: tourTravel.name,
        fromPosition: tourTravel.fromPosition,
        toPosition: tourTravel.toPosition,
        travelDistance: tourTravel.travelDistance,
      },
    });

  const fromPosition = watch('fromPosition');
  const toPosition = watch('toPosition');

  useEffect(() => {
    const connexion = tourTravel.connexionTypeWithEvent;
    const aPositionChanged =
      formState.dirtyFields.fromPosition || formState.dirtyFields.toPosition;

    // toPosition acts as an optional override of the event location (e.g. a
    // stopover city); when set it replaces the event coordinates for incoming.
    const incomingToCoordinates =
      toPosition?.coordinates ?? toEvent?.position?.coordinates;

    if (
      connexion === 'incoming' &&
      fromPosition &&
      incomingToCoordinates &&
      aPositionChanged
    ) {
      setIsDistanceComputing(true);
      calculateDistanceBetweenCoordinates(
        fromPosition.coordinates,
        incomingToCoordinates,
      ).then((d) => {
        setValue('travelDistance', d);
        setIsDistanceComputing(false);
      });
    }

    // fromPosition acts as an optional override of the event location for
    // outgoing travels.
    const outgoingFromCoordinates =
      fromPosition?.coordinates ?? fromEvent?.position?.coordinates;

    if (
      connexion === 'outgoing' &&
      outgoingFromCoordinates &&
      toPosition &&
      aPositionChanged
    ) {
      setIsDistanceComputing(true);
      calculateDistanceBetweenCoordinates(
        outgoingFromCoordinates,
        toPosition.coordinates,
      ).then((d) => {
        setValue('travelDistance', d);
        setIsDistanceComputing(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPosition, toPosition]);

  const onSubmit = async (data: FormInputs) => {
    await dispatch(
      editTourTravel({
        tourTravel: {
          ...tourTravel,
          name: data.name,
          travelDistance: parseFloat(data.travelDistance.toString()),
          fromPosition: data.fromPosition,
          toPosition: data.toPosition,
        },
      }),
    ).unwrap();
    onSaved();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{ gap: 1 }}>
        <FSTextField
          label={translate('ProjectsTour.TourTravelForm.name')}
          register={{ ...register('name') }}
          size="small"
          sx={{ width: '30rem' }}
        />
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          {tourTravel.connexionTypeWithEvent === 'incoming' && (
            <>
              <Box sx={{ width: '20rem', flexShrink: 0 }}>
                <Stack
                  sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}
                >
                  <FormLabel sx={{ fontSize: '0.8rem' }}>
                    {translate('ProjectsTour.TourTravelForm.from-position')}
                  </FormLabel>
                  <PositionFillFromHistoryButton
                    positions={positionHistoryListIncoming}
                    onClickItem={(pos) =>
                      setValue('fromPosition', pos, { shouldDirty: true })
                    }
                  />
                </Stack>
                <Controller
                  name="fromPosition"
                  control={control}
                  render={({ field }) => (
                    <FSPositionGeocodeField
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  )}
                />
              </Box>
              <Controller
                name="toPosition"
                control={control}
                render={({ field }) => (
                  <EventPositionField
                    label={translate('ProjectsTour.TourTravelForm.to-event')}
                    eventPositionName={toEvent?.position?.properties.name}
                    overridePosition={field.value}
                    onOverridePositionChange={field.onChange}
                  />
                )}
              />
            </>
          )}
          {tourTravel.connexionTypeWithEvent === 'direct' && (
            <>
              <Box sx={{ width: '20rem', flexShrink: 0 }}>
                <FormLabel sx={{ fontSize: '0.8rem' }}>
                  {translate('ProjectsTour.TourTravelForm.from-event')}
                </FormLabel>
                <FSTextField
                  disabled
                  value={fromEvent?.position?.properties.name}
                  size="small"
                />
              </Box>
              <Box sx={{ width: '20rem', flexShrink: 0 }}>
                <FormLabel sx={{ fontSize: '0.8rem' }}>
                  {translate('ProjectsTour.TourTravelForm.to-event')}
                </FormLabel>
                <FSTextField
                  disabled
                  value={toEvent?.position?.properties.name}
                  size="small"
                />
              </Box>
            </>
          )}
          {tourTravel.connexionTypeWithEvent === 'outgoing' && (
            <>
              <Controller
                name="fromPosition"
                control={control}
                render={({ field }) => (
                  <EventPositionField
                    label={translate('ProjectsTour.TourTravelForm.from-event')}
                    eventPositionName={fromEvent?.position?.properties.name}
                    overridePosition={field.value}
                    onOverridePositionChange={field.onChange}
                  />
                )}
              />
              <Box sx={{ width: '20rem', flexShrink: 0 }}>
                <Stack
                  sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}
                >
                  <FormLabel sx={{ fontSize: '0.8rem' }}>
                    {translate('ProjectsTour.TourTravelForm.to-position')}
                  </FormLabel>
                  <PositionFillFromHistoryButton
                    positions={positionHistoryListOutcoming}
                    onClickItem={(pos) =>
                      setValue('toPosition', pos, { shouldDirty: true })
                    }
                  />
                </Stack>
                <Controller
                  name="toPosition"
                  control={control}
                  render={({ field }) => (
                    <FSPositionGeocodeField
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  )}
                />
              </Box>
            </>
          )}
          <Box sx={{ width: '7rem', flexShrink: 0 }}>
            <FormLabel sx={{ fontSize: '0.8rem' }}>
              {translate('ProjectsTour.TourTravelForm.travel-distance')}
            </FormLabel>
            <FSTextField
              register={{ ...register('travelDistance') }}
              size="small"
            />
          </Box>
          <Box sx={{ mt: 2.5, flexShrink: 0 }}>
            <FSPrimaryButton type="submit" disabled={isDistanceComputing}>
              {translate('Form.save')}
            </FSPrimaryButton>
          </Box>
        </Stack>
      </Stack>
    </form>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getFromLabel(
  tourTravel: TourTravel,
  fromEvent: ReturnType<typeof Array.prototype.find>,
): string | undefined {
  switch (tourTravel.connexionTypeWithEvent) {
    case 'incoming':
      return tourTravel.fromPosition?.properties.name;
    case 'outgoing':
      return (
        tourTravel.fromPosition?.properties.name ??
        fromEvent?.position?.properties.name
      );
    case 'direct':
      return fromEvent?.position?.properties.name;
  }
}

function getToLabel(
  tourTravel: TourTravel,
  toEvent: ReturnType<typeof Array.prototype.find>,
): string | undefined {
  switch (tourTravel.connexionTypeWithEvent) {
    case 'incoming':
      return (
        tourTravel.toPosition?.properties.name ??
        toEvent?.position?.properties.name
      );
    case 'direct':
      return toEvent?.position?.properties.name;
    case 'outgoing':
      return tourTravel.toPosition?.properties.name;
  }
}
